import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, finalize, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { EMPTY, Observable, Subject, timer } from 'rxjs';
import { ReleaseNote, ReleaseNotesPage } from '../interfaces/release-note.interface';
import { ReleaseNotesService } from '../services/release-notes.service';
import {
    RegenerateReleaseNote,
    StopAdminReleaseNotesPolling,
    DeleteReleaseNote,
    GenerateReleaseNote,
    LoadAdminReleaseNotes,
    LoadRecentReleaseNote,
    LoadReleaseNotes,
    PublishReleaseNote,
    UnpublishReleaseNote,
    UpdateReleaseNote
} from './release-notes.actions';

export interface ReleaseNotesStateModel {
    releaseNotes: ReleaseNote[];
    latestReleaseNote: ReleaseNote | null;
    adminReleaseNotes: ReleaseNote[];
    adminPollingExpired: boolean;
    hasMore: boolean;
    loadedCount: number;
    isLoading: boolean;
    error: string | null;
    adminError: string | null;
    isLoadingMore: boolean;
}
const defaults: ReleaseNotesStateModel = {
    releaseNotes: [],
    latestReleaseNote: null,
    adminReleaseNotes: [],
    adminPollingExpired: false,
    hasMore: false,
    loadedCount: 0,
    isLoading: false,
    error: null,
    adminError: null,
    isLoadingMore: false
};

@State<ReleaseNotesStateModel>({ name: 'releaseNotes', defaults })
@Injectable()
export class ReleaseNotesState {
    private readonly destroyRef = inject(DestroyRef);
    private readonly stopPolling$ = new Subject<void>();
    private publicRequestId = 0;
    private adminActive = false;
    private readonly service = inject(ReleaseNotesService);
    @Selector() static releaseNotes(state: ReleaseNotesStateModel): ReleaseNote[] {
        return state.releaseNotes;
    }
    @Selector() static latestReleaseNote(state: ReleaseNotesStateModel): ReleaseNote | null {
        return state.latestReleaseNote;
    }
    @Selector() static adminReleaseNotes(state: ReleaseNotesStateModel): ReleaseNote[] {
        return state.adminReleaseNotes;
    }
    @Selector() static adminPollingExpired(state: ReleaseNotesStateModel): boolean {
        return state.adminPollingExpired;
    }
    @Selector() static isLoading(state: ReleaseNotesStateModel): boolean {
        return state.isLoading;
    }
    @Selector() static hasMore(state: ReleaseNotesStateModel): boolean {
        return state.hasMore;
    }
    @Selector() static loadedCount(state: ReleaseNotesStateModel): number {
        return state.loadedCount;
    }
    @Selector() static error(state: ReleaseNotesStateModel): string | null {
        return state.error;
    }

    @Selector() static adminError(state: ReleaseNotesStateModel): string | null {
        return state.adminError;
    }
    @Selector() static isLoadingMore(state: ReleaseNotesStateModel): boolean {
        return state.isLoadingMore;
    }

    @Action(LoadReleaseNotes, { cancelUncompleted: true }) load(
        ctx: StateContext<ReleaseNotesStateModel>,
        action = new LoadReleaseNotes()
    ): Observable<ReleaseNote[] | ReleaseNotesPage> {
        const requestId = ++this.publicRequestId;
        if (!action.query)
            return this.request(
                ctx,
                this.service.findAll(),
                (notes) => ({
                    releaseNotes: notes,
                    loadedCount: notes.length
                }),
                false,
                true,
                false,
                requestId
            );
        return this.request(
            ctx,
            this.service.findPage(action.query),
            (page) => {
                const previous = action.append ? ctx.getState().releaseNotes : [];
                return {
                    releaseNotes: [...previous, ...page.items],
                    // Track how many items the server returned, independent of any client-side filtering,
                    // so the next page offset stays correct.
                    loadedCount: (action.append ? ctx.getState().loadedCount : 0) + page.items.length,
                    hasMore: page.hasMore
                };
            },
            false,
            true,
            action.append,
            requestId
        );
    }
    @Action(LoadRecentReleaseNote) recent(ctx: StateContext<ReleaseNotesStateModel>): Observable<ReleaseNote | null> {
        return this.request(ctx, this.service.getRecent(), (note) => ({ latestReleaseNote: note }), false, true);
    }
    @Action(StopAdminReleaseNotesPolling) stopPolling(): void {
        this.adminActive = false;
        this.stopPolling$.next();
    }
    @Action(LoadAdminReleaseNotes, { cancelUncompleted: true }) admin(
        ctx: StateContext<ReleaseNotesStateModel>,
        action = new LoadAdminReleaseNotes()
    ): Observable<ReleaseNote[]> {
        this.adminActive = true;
        ctx.patchState({ adminPollingExpired: false });
        return this.request(
            ctx,
            this.service.getAllForAdmin(),
            (notes) => ({ adminReleaseNotes: notes }),
            false,
            false,
            false,
            undefined,
            action.preserveError
        ).pipe(
            switchMap((notes) =>
                notes.some((note) => note.generationStatus === 'pending' || note.generationStatus === 'generating')
                    ? timer(5000, 5000).pipe(
                          switchMap(() => this.service.getAllForAdmin()),
                          tap((updated) => ctx.patchState({ adminReleaseNotes: updated })),
                          takeWhile(
                              (updated) =>
                                  updated.some(
                                      (note) =>
                                          note.generationStatus === 'pending' || note.generationStatus === 'generating'
                                  ),
                              true
                          ),
                          takeUntil(
                              timer(15 * 60_000).pipe(
                                  tap(() =>
                                      ctx.patchState({
                                          adminPollingExpired: true,
                                          adminReleaseNotes: [...ctx.getState().adminReleaseNotes]
                                      })
                                  )
                              )
                          ),
                          catchError(() => {
                              ctx.patchState({ adminError: 'release-notes.error' });
                              return EMPTY;
                          })
                      )
                    : EMPTY
            ),
            takeUntil(this.stopPolling$),
            takeUntilDestroyed(this.destroyRef)
        );
    }
    @Action(RegenerateReleaseNote) regenerate(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: RegenerateReleaseNote
    ): Observable<ReleaseNote> {
        return this.request(ctx, this.service.regenerate(action.id), () => ({}), true);
    }
    @Action(GenerateReleaseNote) generate(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: GenerateReleaseNote
    ): Observable<ReleaseNote> {
        return this.request(
            ctx,
            this.service.generate({ version: action.version, rawChangelogData: action.rawChangelogData }),
            () => ({}),
            true,
            false,
            false,
            undefined,
            false,
            true
        );
    }
    @Action(UpdateReleaseNote) update(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: UpdateReleaseNote
    ): Observable<ReleaseNote> {
        return this.request(ctx, this.service.update(action.id, action.note), () => ({}), true);
    }
    @Action(PublishReleaseNote) publish(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: PublishReleaseNote
    ): Observable<ReleaseNote> {
        return this.request(ctx, this.service.publish(action.id), (note) => ({
            adminReleaseNotes: ctx
                .getState()
                .adminReleaseNotes.map((existing) => (existing.id === note.id ? note : existing))
        }));
    }
    @Action(UnpublishReleaseNote) unpublish(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: UnpublishReleaseNote
    ): Observable<ReleaseNote> {
        return this.request(ctx, this.service.unpublish(action.id), () => ({}), true);
    }
    @Action(DeleteReleaseNote) remove(
        ctx: StateContext<ReleaseNotesStateModel>,
        action: DeleteReleaseNote
    ): Observable<void> {
        return this.request(ctx, this.service.remove(action.id), () => ({}), true);
    }

    private request<T>(
        ctx: StateContext<ReleaseNotesStateModel>,
        request: Observable<T>,
        patch: (value: T) => Partial<ReleaseNotesStateModel>,
        reloadAdmin = false,
        publicRequest = false,
        append = false,
        requestId?: number,
        preserveError = false,
        duplicateVersionConflict = false
    ): Observable<T> {
        let succeeded = false;
        let reloadAfterError = false;
        const errorSlot = publicRequest ? 'error' : 'adminError';
        ctx.patchState({ isLoading: !append, isLoadingMore: append, ...(!preserveError ? { [errorSlot]: null } : {}) });
        const isCurrent = () => requestId === undefined || requestId === this.publicRequestId;
        return request.pipe(
            tap((value) => {
                if (!isCurrent()) return;
                succeeded = true;
                ctx.patchState(patch(value));
            }),
            catchError((error: { status?: number; error?: { message?: string } }) => {
                if (!isCurrent()) return EMPTY;
                reloadAfterError = reloadAdmin && error.status === 503;
                ctx.patchState({
                    [errorSlot]:
                        error.status === 409 && duplicateVersionConflict
                            ? 'release-notes.error-duplicate-version'
                            : (error.error?.message ?? 'release-notes.error')
                });
                return EMPTY;
            }),
            finalize(() => {
                if (isCurrent()) ctx.patchState({ isLoading: false, isLoadingMore: false });
                if (reloadAdmin && (succeeded || reloadAfterError) && this.adminActive)
                    void ctx.dispatch(new LoadAdminReleaseNotes(reloadAfterError));
            })
        );
    }
}
